import React, { useMemo } from "react";
import { motion } from "framer-motion";

/**
 * ParticleField — Floating golden/cream particles
 * Apple-inspired ambient motion, performant with transform-only animations
 */

const PARTICLE_COUNT = 20;

function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export default function ParticleField() {
  const particles = useMemo(() => {
    const rand = mulberry32(42);
    
    return Array.from({ length: PARTICLE_COUNT }).map((_, i) => {
      // Distribute particles more densely at bottom, fading toward top
      const verticalBias = Math.pow(rand(), 0.6); // Bias toward lower values (bottom)
      
      return {
        id: i,
        x: rand() * 100, // percentage across width
        y: 20 + verticalBias * 70, // percentage down from top (20% to 90%)
        size: 2 + rand() * 2.5, // 2px to 4.5px
        isGold: rand() > 0.4, // 60% gold, 40% cream
        opacity: 0.25 + rand() * 0.4, // 0.25 to 0.65
        // Animation properties
        floatDuration: 30 + rand() * 30, // 30-60s for upward drift
        floatDelay: rand() * -20, // Negative delay for immediate movement
        swayDuration: 8 + rand() * 6, // 8-14s for horizontal sway
        pulseDuration: 4 + rand() * 3, // 4-7s for opacity pulse
        pulseDelay: rand() * 4,
        // Movement amplitude
        swayAmount: 15 + rand() * 25, // pixels to sway
        floatDistance: 80 + rand() * 120, // pixels to rise
      };
    });
  }, []);

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden"
      style={{ zIndex: 1 }}
      aria-hidden="true"
    >
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full will-change-transform"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            background: p.isGold 
              ? "rgba(201, 168, 76, 0.9)" 
              : "rgba(240, 235, 225, 0.85)",
            boxShadow: p.isGold
              ? `0 0 ${p.size * 3}px rgba(201, 168, 76, 0.4)`
              : `0 0 ${p.size * 2}px rgba(240, 235, 225, 0.2)`,
          }}
          initial={{ 
            opacity: p.opacity, 
            y: 0, 
            x: 0,
            scale: 1 
          }}
          animate={{
            // Primary: Gentle upward float
            y: [0, -p.floatDistance, -p.floatDistance * 0.8],
            // Secondary: Horizontal sway
            x: [0, p.swayAmount, -p.swayAmount * 0.5, 0],
            // Tertiary: Subtle scale pulse
            scale: [1, 1.1, 0.95, 1],
            // Quaternary: Opacity modulation
            opacity: [p.opacity, p.opacity * 1.3, p.opacity * 0.7, p.opacity],
          }}
          transition={{
            y: {
              duration: p.floatDuration,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
              delay: p.floatDelay,
            },
            x: {
              duration: p.swayDuration,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
              delay: p.floatDelay,
            },
            scale: {
              duration: p.pulseDuration,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
              delay: p.pulseDelay,
            },
            opacity: {
              duration: p.pulseDuration * 1.5,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
              delay: p.pulseDelay,
            },
          }}
        />
      ))}
    </div>
  );
}
